import { useAtomValue } from 'jotai';
import { lazy, Suspense } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { OktaAuth } from '@okta/okta-auth-js'; // Correct import
import { LoginCallback, Security } from '@okta/okta-react';
import { Group } from '@optiaxiom/react';

import {
  ConnectionProvider,
  FeatureFlag,
  FeatureFlagProvider,
  SelfConnectionProvider,
  SocketProvider,
} from '@frontend/contexts';

import CanUser from './components/CanUser';
import CapabilityOrFlagGuard from './components/CapabilityOrFlagGuard';
import { ErrorBoundary } from './components/error-boundary';
import FullScreenLoader from './components/FullScreenLoader';
import { OptiGlobalHeader } from './components/optiGlobalHeader';
import { NotificationBellButton } from './components/optiGlobalHeader/components/NotificationBellButton';
import { OpalOverlayButton } from './components/optiGlobalHeader/components/OpalOverlayButton';
import PathNavigator from './components/PathNavigator';
import RequireAuth from './components/RequireAuth'; // Custom auth wrapper for protected pages
import RequireAuthOnly from './components/RequireAuthOnly';
import { BaseLayout } from './layouts/baseLayout';
import { isDesktopApp } from './utils/desktop';
import { SentryErrorBoundary } from './utils/sentry';
import { globalOptiHeaderVisibleAtom } from './atoms';
import { FeatureFlags, OVERLAY_PAGE_SEGMENTS, UxFeaturesVariables } from './constants';
import { useFontSize, useInstanceId, useOpalUser, useTheme, useUserPreferences } from './hooks';
import oktaAuth from './oktaAuth';

const AgentPage = lazy(() =>
  import(/* webpackChunkName: "AgentPage" */ './features').then(m => ({ default: m.AgentPage }))
);
const Chat = lazy(() =>
  import(/* webpackChunkName: "Chat" */ './features/chat').then(m => ({ default: m.Chat }))
);
const InstructionPage = lazy(() =>
  import(/* webpackChunkName: "InstructionPage" */ './features/userInstructions/components').then(
    m => ({ default: m.InstructionPage })
  )
);
const ContextPage = lazy(() =>
  import(/* webpackChunkName: "ContextPage" */ './features/context').then(m => ({
    default: m.ContextPage,
  }))
);
const SettingsPage = lazy(
  () => import(/* webpackChunkName: "SettingsPage" */ './features/settings/Settings')
);
const NotificationCenterPage = lazy(() =>
  import(/* webpackChunkName: "NotificationCenterPage" */ './features/notifications').then(m => ({
    default: m.NotificationCenterPage,
  }))
);
const ArtifactsPage = lazy(() =>
  import(/* webpackChunkName: "ArtifactsPage" */ './features/artifacts').then(m => ({
    default: m.ArtifactsPage,
  }))
);
const PluginsPage = lazy(() =>
  import(/* webpackChunkName: "PluginsPage" */ './features/plugins/components/PluginsPage').then(
    m => ({ default: m.PluginsPage })
  )
);
const ToolsManagement = lazy(() =>
  import(/* webpackChunkName: "ToolsManagement" */ './features').then(m => ({
    default: m.ToolsManagement,
  }))
);
const WorkflowExecutionPage = lazy(() =>
  import(
    /* webpackChunkName: "WorkflowExecutionPage" */ './features/agents/components/workflow-execution'
  ).then(m => ({
    default: m.WorkflowExecutionPage,
  }))
);
const ToolsWarningBanner = lazy(() =>
  import(
    /* webpackChunkName: "ToolsWarningBanner" */ './features/tools-management/components/ToolsWarningBanner/ToolsWarningBanner'
  ).then(m => ({
    default: m.ToolsWarningBanner,
  }))
);
const RedirectHandler = lazy(
  () => import(/* webpackChunkName: "RedirectHandler" */ './components/RedirectHandler')
);
const InstancePicker = lazy(() =>
  import(/* webpackChunkName: "InstancePicker" */ './features/instancePicker').then(m => ({
    default: m.InstancePicker,
  }))
);

function App() {
  return (
    <SentryErrorBoundary fallback={<p>An error has occurred</p>}>
      <WrappedSecurity />
    </SentryErrorBoundary>
  );
}

function WrappedSecurity() {
  const navigate = useNavigate();
  const location = useLocation();
  const { instanceId } = useInstanceId();
  const globalOptiHeaderVisible = useAtomValue(globalOptiHeaderVisibleAtom);
  useTheme();
  useFontSize();
  // Seed font_size + voice preferences atoms from the backend so settings
  // persist across browsers and devices, not just across refreshes on
  // the same machine (which atomWithStorage already handles).
  const { opalUser } = useOpalUser();
  useUserPreferences(opalUser.email);

  const segments = location.pathname.split('/');
  const isOnOverlayPage = OVERLAY_PAGE_SEGMENTS.some(s => segments.includes(s));
  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri?: string): Promise<void> => {
    navigate(originalUri || '/');
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <Routes>
        <Route path="/login" element={<LoginCallback />} />
        <Route
          path="/instances"
          element={
            <RequireAuthOnly>
              <Suspense fallback={<FullScreenLoader />}>
                <InstancePicker />
              </Suspense>
            </RequireAuthOnly>
          }
        />
        <Route
          path="/redirect"
          element={
            <RequireAuthOnly>
              <Suspense fallback={<FullScreenLoader />}>
                <RedirectHandler />
              </Suspense>
            </RequireAuthOnly>
          }
        />
        <Route
          path="/instances/:instanceId"
          element={
            <RequireAuth>
              <FeatureFlagProvider>
                <ErrorBoundary>
                  <OptiGlobalHeader visible={globalOptiHeaderVisible && !isDesktopApp()}>
                    <Group gap="8">
                      <NotificationBellButton />
                      {isOnOverlayPage && <OpalOverlayButton />}
                    </Group>
                  </OptiGlobalHeader>
                </ErrorBoundary>
                <SocketProvider>
                  <Suspense fallback={null}>
                    <ToolsWarningBanner />
                  </Suspense>
                  <BaseLayout />
                </SocketProvider>
              </FeatureFlagProvider>
            </RequireAuth>
          }
        >
          <Route
            path="chat/*"
            element={
              <CanUser operations={['use_chat']}>
                <SelfConnectionProvider>
                  <Suspense fallback={<FullScreenLoader />}>
                    <Chat />
                  </Suspense>
                </SelfConnectionProvider>
              </CanUser>
            }
          />
          <Route
            path="instructions/*"
            element={
              <CanUser operations={['view_instructions', 'manage_instructions', 'use_chat']}>
                <ConnectionProvider>
                  <Suspense fallback={<FullScreenLoader />}>
                    <InstructionPage />
                  </Suspense>
                </ConnectionProvider>
              </CanUser>
            }
          />
          <Route
            path="skills/*"
            element={
              <CanUser operations={['view_instructions', 'manage_instructions', 'use_chat']}>
                <ConnectionProvider>
                  <Suspense fallback={<FullScreenLoader />}>
                    <InstructionPage />
                  </Suspense>
                </ConnectionProvider>
              </CanUser>
            }
          />
          <Route
            path="context/*"
            element={
              <CanUser operations={['view_instructions', 'manage_instructions', 'use_chat']}>
                <FeatureFlag
                  featureKey={FeatureFlags.UX_FEATURES}
                  variable={UxFeaturesVariables.REORG_RAG_CONTEXT}
                >
                  <Suspense fallback={<FullScreenLoader />}>
                    <ContextPage />
                  </Suspense>
                </FeatureFlag>
              </CanUser>
            }
          />
          <Route
            path="artifacts/*"
            element={
              <CanUser operations={['use_chat']}>
                <FeatureFlag featureKey="opal_artifacts">
                  <Suspense fallback={<FullScreenLoader />}>
                    <ArtifactsPage />
                  </Suspense>
                </FeatureFlag>
              </CanUser>
            }
          />
          <Route
            path="tools/*"
            element={
              <CanUser operations={['view_tools', 'use_chat']}>
                <Suspense fallback={<FullScreenLoader />}>
                  <ToolsManagement />
                </Suspense>
              </CanUser>
            }
          />
          <Route
            path="settings/*"
            element={
              <CanUser operations={['use_chat']}>
                <Suspense fallback={<FullScreenLoader />}>
                  <SettingsPage />
                </Suspense>
              </CanUser>
            }
          />
          <Route
            path="notifications/*"
            element={
              <Suspense fallback={<FullScreenLoader />}>
                <NotificationCenterPage />
              </Suspense>
            }
          />
          <Route
            path="agents/workflow/executions/:executionId/*"
            element={
              <CapabilityOrFlagGuard
                capabilities={['workflows_full_enabled', 'workflows_chat_enabled']}
                featureFlags={['workflow-agent']}
              >
                <CanUser operations={['view_agents']}>
                  <Suspense fallback={<FullScreenLoader />}>
                    <WorkflowExecutionPage />
                  </Suspense>
                </CanUser>
              </CapabilityOrFlagGuard>
            }
          />
          <Route
            path="agents/*"
            element={
              <CanUser operations={['view_agents']}>
                <SelfConnectionProvider>
                  <Suspense fallback={<FullScreenLoader />}>
                    <AgentPage />
                  </Suspense>
                </SelfConnectionProvider>
              </CanUser>
            }
          />
          <Route
            path="plugins/*"
            element={
              <CanUser operations={['manage_instructions', 'manage_specialized_agents']}>
                <FeatureFlag
                  featureKey={FeatureFlags.PLUGINS}
                  fallback={<PathNavigator path={`/instances/${instanceId}/chat`} />}
                >
                  <Suspense fallback={<FullScreenLoader />}>
                    <PluginsPage />
                  </Suspense>
                </FeatureFlag>
              </CanUser>
            }
          />
          <Route path="" element={<PathNavigator path={`/instances/${instanceId}/chat`} />} />
        </Route>
        <Route
          path="*"
          element={
            !instanceId ? (
              <RequireAuthOnly>
                <PathNavigator path="/instances" />
              </RequireAuthOnly>
            ) : (
              <RequireAuth>
                <PathNavigator path={`/instances/${instanceId}/chat`} />
              </RequireAuth>
            )
          }
        />
      </Routes>
    </Security>
  );
}

export default App;
